import { useState, useEffect, useRef } from 'react';
import { Merge, Minus, Plus, Split } from 'lucide-react';
import { PortalDsButton, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import { DEFAULT_BUDGET_TABLE_STRUCTURE, findAdjacentBudgetCell } from '../insertion-order-table';

interface IoBoldsignBudgetTableProps {
  initialData?: string[][];
  onChange: (data: string[][]) => void;
  disabled?: boolean;
}

export function IoBoldsignBudgetTable({
  initialData,
  onChange,
  disabled = false,
}: IoBoldsignBudgetTableProps) {
  const [tableData, setTableData] = useState<string[][]>(() => {
    if (initialData && initialData.length > 0) {
      return initialData;
    }
    return DEFAULT_BUDGET_TABLE_STRUCTURE;
  });

  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ row: number; col: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipBlurCommitRef = useRef(false);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  }, [editingCell]);

  useEffect(() => {
    if (initialData && initialData.length > 0) {
      setTableData(initialData);
    } else {
      setTableData(DEFAULT_BUDGET_TABLE_STRUCTURE);
    }
  }, [initialData]);

  const updateTableData = (newData: string[][]) => {
    setTableData(newData);
    onChange(newData);
  };

  const isCellMerged = (row: number, col: number) => tableData[row][col] === '$MERGE';

  const handleMouseDown = (row: number, col: number, event: React.MouseEvent) => {
    if (disabled || event.button !== 0) return;
    event.preventDefault();
    setIsDragging(true);
    setDragStart({ row, col });
    setSelectedCells(new Set([`${row}-${col}`]));
  };

  const handleMouseEnter = (row: number, col: number) => {
    if (!isDragging || !dragStart || disabled) return;
    if (col !== dragStart.col) return;
    const minRow = Math.min(row, dragStart.row);
    const maxRow = Math.max(row, dragStart.row);
    const newSelected = new Set<string>();
    for (let r = minRow; r <= maxRow; r++) {
      newSelected.add(`${r}-${col}`);
    }
    setSelectedCells(newSelected);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  const handleCellClick = (row: number, col: number) => {
    if (disabled || isDragging) return;
    if (editingCell && (editingCell.row !== row || editingCell.col !== col)) {
      const newData = tableData.map((tableRow) => [...tableRow]);
      const currentValue = newData[editingCell.row][editingCell.col];
      const isMerged = currentValue.startsWith('$MERGE ');
      newData[editingCell.row][editingCell.col] = isMerged ? `$MERGE ${editValue}` : editValue;
      updateTableData(newData);
    }
    const cellValue = tableData[row][col];
    setSelectedCells(new Set([`${row}-${col}`]));
    const cleanValue = cellValue.startsWith('$MERGE ') ? cellValue.substring(7) : cellValue;
    setEditValue(cleanValue);
    setEditingCell({ row, col });
  };

  const handleCellDoubleClick = (row: number, col: number) => {
    if (disabled) return;
    const cellValue = tableData[row][col];
    setSelectedCells(new Set([`${row}-${col}`]));
    const cleanValue = cellValue.startsWith('$MERGE ') ? cellValue.substring(7) : cellValue;
    setEditValue(cleanValue);
    setEditingCell({ row, col });
  };

  const commitEditingValue = (data: string[][]) => {
    if (!editingCell) return data;
    const newData = data.map((tableRow) => [...tableRow]);
    const currentValue = newData[editingCell.row][editingCell.col];
    const isMerged = currentValue.startsWith('$MERGE ');
    newData[editingCell.row][editingCell.col] = isMerged ? `$MERGE ${editValue}` : editValue;
    return newData;
  };

  const startEditingCell = (row: number, col: number, data: string[][]) => {
    const cellValue = data[row][col];
    setSelectedCells(new Set([`${row}-${col}`]));
    const cleanValue = cellValue.startsWith('$MERGE ') ? cellValue.substring(7) : cellValue;
    setEditValue(cleanValue);
    setEditingCell({ row, col });
  };

  const handleEditComplete = () => {
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      return;
    }
    if (editingCell) {
      updateTableData(commitEditingValue(tableData));
      setEditingCell(null);
      setEditValue('');
      setSelectedCells(new Set());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && editingCell) {
      const nextCell = findAdjacentBudgetCell(
        tableData,
        editingCell.row,
        editingCell.col,
        e.shiftKey ? -1 : 1
      );
      if (!nextCell) {
        return;
      }
      e.preventDefault();
      skipBlurCommitRef.current = true;
      const committed = commitEditingValue(tableData);
      updateTableData(committed);
      startEditingCell(nextCell.row, nextCell.col, committed);
      return;
    }
    if (e.key === 'Enter') {
      handleEditComplete();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  const addRow = () => {
    if (disabled) return;
    const newRow = new Array(tableData[0].length).fill('');
    updateTableData([...tableData, newRow]);
  };

  const addColumn = () => {
    if (disabled) return;
    updateTableData(tableData.map((row) => [...row, '']));
  };

  const deleteRow = (rowIndex: number) => {
    if (disabled || tableData.length <= 1) return;
    updateTableData(tableData.filter((_, index) => index !== rowIndex));
    setSelectedCells(new Set());
    setEditingCell(null);
  };

  const deleteColumn = (colIndex: number) => {
    if (disabled || tableData[0].length <= 1) return;
    updateTableData(tableData.map((row) => row.filter((_, index) => index !== colIndex)));
    setSelectedCells(new Set());
    setEditingCell(null);
  };

  const mergeCellsVertically = () => {
    if (disabled || selectedCells.size < 2) return;

    const cellPositions = Array.from(selectedCells).map((key) => {
      const [row, col] = key.split('-').map(Number);
      return { row, col };
    });
    cellPositions.sort((a, b) => a.row - b.row);
    const newData = tableData.map((row) => [...row]);
    const firstCell = cellPositions[0];

    const combinedText = cellPositions
      .map((pos) => {
        const value = newData[pos.row][pos.col];
        let cleanValue = value;
        if (value.startsWith('$MERGE ')) {
          cleanValue = value.substring(7);
        } else if (value === '$MERGE') {
          cleanValue = '';
        }
        return cleanValue.trim();
      })
      .filter((text) => text.length > 0)
      .join(' ');
    newData[firstCell.row][firstCell.col] = `$MERGE ${combinedText}`;

    for (let i = 1; i < cellPositions.length; i++) {
      const pos = cellPositions[i];
      newData[pos.row][pos.col] = '$MERGE';
    }

    updateTableData(newData);
    setSelectedCells(new Set());
  };

  const unmergeCells = () => {
    if (disabled) return;

    const newData = tableData.map((row) => [...row]);
    let hasChanges = false;

    selectedCells.forEach((cellKey) => {
      const [row, col] = cellKey.split('-').map(Number);
      const cellValue = newData[row][col];

      if (cellValue === '$MERGE') {
        newData[row][col] = '';
        hasChanges = true;
      } else if (cellValue.startsWith('$MERGE ')) {
        newData[row][col] = cellValue.substring(7);
        for (let r = row + 1; r < newData.length; r++) {
          if (newData[r][col] === '$MERGE') {
            newData[r][col] = '';
            hasChanges = true;
          } else {
            break;
          }
        }
        hasChanges = true;
      }
    });

    if (hasChanges) {
      updateTableData(newData);
      setSelectedCells(new Set());
      setEditingCell(null);
      setEditValue('');
    }
  };

  const getCellDisplayValue = (row: number, col: number) => {
    const value = tableData[row][col];
    if (value === '$MERGE') return '';
    if (value.startsWith('$MERGE ')) return value.substring(7);
    return value;
  };

  const getMergedCellSpan = (row: number, col: number) => {
    if (isCellMerged(row, col)) return 0;

    let span = 1;
    for (let r = row + 1; r < tableData.length; r++) {
      if (tableData[r][col] === '$MERGE') {
        span++;
      } else {
        break;
      }
    }
    return span;
  };

  const getCellClassName = (row: number, col: number) => {
    const cellKey = `${row}-${col}`;
    const isSelected = selectedCells.has(cellKey);
    const isMerged = isCellMerged(row, col);
    const isHeader = row === 0;
    const isEditing = editingCell?.row === row && editingCell?.col === col;

    return cn(
      'relative min-h-5 cursor-pointer select-none border border-[#D9DBE3] p-2 text-[13.5px] text-[#0D0F1A] transition-colors',
      isHeader && 'bg-[#F7F8FA] text-[11px] font-bold uppercase tracking-[0.5px] text-[#9CA3AF]',
      isSelected && 'z-10 border-2 border-[#2563EB] bg-[#EFF6FF]',
      isMerged && 'bg-[#EDEEF2] opacity-50',
      isEditing && 'bg-white',
      disabled && 'cursor-default',
      !isMerged && getMergedCellSpan(row, col) > 1 && 'align-middle'
    );
  };

  const hasMergeableCells = () => {
    if (selectedCells.size < 2) return false;

    if (editingCell) {
      const editingCellKey = `${editingCell.row}-${editingCell.col}`;
      if (selectedCells.has(editingCellKey)) {
        return false;
      }
    }

    const nonMergedCells = Array.from(selectedCells).filter((cellKey) => {
      const [row, col] = cellKey.split('-').map(Number);
      return !isCellMerged(row, col);
    });

    return nonMergedCells.length >= 2;
  };

  const hasMergedCells = () => {
    return Array.from(selectedCells).some((cellKey) => {
      const [row, col] = cellKey.split('-').map(Number);
      const cellValue = tableData[row][col];

      if (cellValue === '$MERGE' || cellValue.startsWith('$MERGE ')) {
        return true;
      }

      for (let r = row + 1; r < tableData.length; r++) {
        if (tableData[r][col] === '$MERGE') {
          return true;
        }
        break;
      }

      return false;
    });
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging]);

  const chromeButtonClass = 'h-[18px] w-[18px] rounded-full border-transparent bg-[#EDEEF2] p-0 text-[#5E6278] hover:bg-[#D9DBE3] hover:text-[#0D0F1A]';

  return (
    <div>
      <div className="relative mt-4">
        <div className="overflow-hidden rounded-xl border border-[#EDEEF2]">
          <table className="w-full border-collapse">
            {!disabled && (
              <thead>
                <tr className="bg-[#F7F8FA]">
                  <td className="w-8 border-0 p-0 text-center" />
                  {tableData[0].map((_, colIndex) => (
                    <td
                      key={colIndex}
                      className="border-0 py-1 text-center"
                    >
                      <div className="flex h-full items-center justify-center">
                        <PortalDsButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteColumn(colIndex)}
                          className={chromeButtonClass}
                          disabled={tableData[0].length <= 1}
                          aria-label="Delete column"
                        >
                          <Minus className="h-3 w-3" />
                        </PortalDsButton>
                      </div>
                    </td>
                  ))}
                  <td className="w-8 border-0 p-0 text-center">
                    <div className="ml-2 flex h-full items-center justify-center">
                      <PortalDsButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={addColumn}
                        className={chromeButtonClass}
                        aria-label="Add column"
                      >
                        <Plus className="h-3 w-3" />
                      </PortalDsButton>
                    </div>
                  </td>
                </tr>
              </thead>
            )}

            <tbody>
              {tableData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {!disabled && (
                    <td className="w-8 border-0 bg-[#F7F8FA] p-0 text-center">
                      <div className="flex h-full items-center justify-center">
                        <PortalDsButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteRow(rowIndex)}
                          className={chromeButtonClass}
                          disabled={tableData.length <= 1}
                          aria-label="Delete row"
                        >
                          <Minus className="h-3 w-3" />
                        </PortalDsButton>
                      </div>
                    </td>
                  )}

                  {row.map((_, colIndex) => {
                    const span = getMergedCellSpan(rowIndex, colIndex);
                    if (span === 0) return null;

                    return (
                      <td
                        key={`${rowIndex}-${colIndex}`}
                        className={getCellClassName(rowIndex, colIndex)}
                        rowSpan={span}
                        onMouseDown={(e) => handleMouseDown(rowIndex, colIndex, e)}
                        onMouseEnter={() => handleMouseEnter(rowIndex, colIndex)}
                        onClick={() => handleCellClick(rowIndex, colIndex)}
                        onDoubleClick={() => handleCellDoubleClick(rowIndex, colIndex)}
                        style={span > 1 ? { minHeight: `${span * 20}px` } : undefined}
                      >
                        {editingCell?.row === rowIndex && editingCell?.col === colIndex ? (
                          <input
                            ref={inputRef}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleEditComplete}
                            onKeyDown={handleKeyDown}
                            className="m-0 h-full min-h-5 w-full border-none bg-transparent p-0 text-[13.5px] outline-none"
                            placeholder="Enter text..."
                          />
                        ) : (
                          <div className="flex h-full min-h-5 items-center justify-start">
                            <span>
                              {getCellDisplayValue(rowIndex, colIndex)}
                            </span>
                          </div>
                        )}
                      </td>
                    );
                  })}

                  {!disabled && (
                    <td className="w-8 border-0" />
                  )}
                </tr>
              ))}

              {!disabled && (
                <tr>
                  <td className="w-8 border-0 bg-[#F7F8FA] p-0 text-center">
                    <div className="mt-2 flex h-full items-center justify-center">
                      <PortalDsButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={addRow}
                        className={chromeButtonClass}
                        aria-label="Add row"
                      >
                        <Plus className="h-3 w-3" />
                      </PortalDsButton>
                    </div>
                  </td>
                  {tableData[0].map((_, colIndex) => (
                    <td
                      key={colIndex}
                      className="border-0"
                    />
                  ))}
                  <td className="w-8 border-0" />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!disabled && (
        <div className="mt-3 flex min-h-10 items-center gap-4 pl-10">
          <span className={portalDs.caption}>
            * Click and drag vertically to select cells for merging.
          </span>

          {selectedCells.size > 0 && (
            <div className="flex gap-2">
              {hasMergeableCells() && (
                <PortalDsButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={mergeCellsVertically}
                >
                  <Merge className="h-4 w-4" />
                  Merge Cells (
                  {selectedCells.size}
                  )
                </PortalDsButton>
              )}

              {hasMergedCells() && (
                <PortalDsButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    unmergeCells();
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <Split className="h-4 w-4" />
                  Unmerge Cells
                </PortalDsButton>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
